// src/components/Dashboard/ClaimDocuments.jsx
//
// Generador de documentos del reclamo (LOR, SPOL y Closing Statement). Descarga el PDF ya
// lleno con los datos del reclamo para enviarlo a firmar y marcar las
// firmas. Nunca bloquea por datos faltantes: los lista para que se decida
// si completarlos antes o generar con esos espacios en blanco.
//
// Hoy vive en la ficha del reclamo (ClaimDetailPage); en la Etapa 3 cada
// documento pasa a ser el módulo de su paso (`signature` en "Firma de la
// LOR", `polGenerator` en "RS / POL" — config/claimFlow.js).
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { downloadPdf } from '../../documents/pdfFill';
import { buildClaimDocument, claimDocumentFileName, renderClaimDocument } from '../../documents/claimDocuments';
import { getStepData } from '../../services/flowService';
import { openBackup } from '../../services/backupService';
import { normalizePolicyDraft } from '../../services/policyService';
import { PolicySummary } from './PolicyReview';
import ClosingBuilder from './ClosingBuilder';
import DocumentSignature from './DocumentSignature';
import DocumentPreflight, { saveDocumentChanges } from './DocumentPreflight';
import AttorneyExportButton from './AttorneyExportButton';
import './ClaimFlow.css';
import { markDocumentGenerated } from '../../services/signedDocumentService';
import { useAuth } from '../../contexts/AuthContext';
import { viewInvoice, printInvoice } from '../../utils/invoiceGenerator';

const ClaimDocuments = ({ claimRecord, language, isAdmin, checks = [], onClaimChange }) => {
  const es = language === 'es';
  // El CSR Assistant también genera y marca la firma de LOR/SPOL (igual que en el paso del flujo).
  const { userRoleKey, can } = useAuth();
  const isTeam = isAdmin || userRoleKey === 'csr_assistant';
  const canSign = isTeam || userRoleKey === 'pa_assistant';
  const [estimate, setEstimate] = useState({});
  const [generating, setGenerating] = useState(null);
  const [policy, setPolicy] = useState(null); // stepData/policy_review: PDF y análisis
  const [preflight, setPreflight] = useState(null); // 'lor' | 'spol': resumen abierto antes de generar

  // Montos del estimado para la SPOL: los guarda el paso "RS / POL" (stepData rs_pol).
  useEffect(() => {
    if (!claimRecord?.id) return;
    getStepData('claim', claimRecord.id, 'rs_pol')
      .then(setEstimate)
      .catch(error => { console.error('Error loading estimate data:', error); setEstimate({}); });
  }, [claimRecord?.id]);

  // La póliza se sube en el paso "Revisión de póliza"; aquí queda para revisarla cuando haga falta.
  useEffect(() => {
    if (!claimRecord?.id) return;
    getStepData('claim', claimRecord.id, 'policy_review')
      .then(data => setPolicy(data || null))
      .catch(() => setPolicy(null));
  }, [claimRecord?.id]);

  const documents = useMemo(() => [
    buildClaimDocument('lor', claimRecord, language),
    buildClaimDocument('spol', claimRecord, language, estimate)
  ], [claimRecord, estimate, language]);

  // Invoices de los cheques del reclamo: siempre a la mano para quien puede verlos.
  const invoices = can('downloadInvoice') ? checks.filter(check => check.invoiceNumber) : [];
  const summary = policy?.status === 'reviewed' && policy.data ? normalizePolicyDraft(policy.data) : policy?.draft ? normalizePolicyDraft(policy.draft) : null;

  if (!claimRecord) return null;

  const generate = async (document, record = claimRecord) => {
    setGenerating(document.id);
    try {
      downloadPdf(await renderClaimDocument(document), claimDocumentFileName(document, record));
      if (canSign) {
        const changes = await markDocumentGenerated(record, document.id.toLowerCase());
        if (changes && onClaimChange) onClaimChange(changes);
      }
    } catch (error) {
      console.error(`Error generating ${document.id}:`, error);
      toast.error(es ? `No se pudo generar la ${document.id}` : `Could not generate the ${document.id}`);
    } finally {
      setGenerating(null);
    }
  };

  // Resumen previo: guarda lo completado y genera con los datos ya actualizados.
  const confirmPreflight = async changes => {
    try {
      const saved = await saveDocumentChanges(claimRecord, estimate, changes);
      if (Object.keys(saved.claimChanges).length && onClaimChange) onClaimChange(saved.claimChanges);
      setEstimate(saved.estimate);
      setPreflight(null);
      await generate(buildClaimDocument(preflight, saved.claimRecord, language, saved.estimate), saved.claimRecord);
    } catch (error) {
      console.error('Error saving document data:', error);
      toast.error(es ? 'No se pudieron guardar los datos' : 'Could not save the data');
    }
  };

  return (
    <div className="cd-detail-panel flow-panel">
      {preflight && (
        <DocumentPreflight docType={preflight} claimRecord={claimRecord} estimate={estimate} language={language}
          onClose={() => setPreflight(null)} onConfirm={confirmPreflight} />
      )}
      <div className="cd-detail-panel-header cd-docs-header">
        <h2><i className="fa-solid fa-folder-open" aria-hidden="true"></i> {es ? 'Documentos generados' : 'Generated documents'}</h2>
        <p className="cd-section-hint">{es ? 'Documentos que la app arma con los datos del reclamo (LOR, Closing Statement, etc.). Los archivos que subes tú van en Archivos adjuntos.' : 'Documents the app builds from the claim data (LOR, Closing Statement, etc.). Files you upload go in Attached files.'}</p>
        {isTeam && <AttorneyExportButton claimRecord={claimRecord} language={language} />}
      </div>
      {policy?.file?.storageKey && (
        <div className="doc-item">
          <div className="doc-item-head">
            <span className="doc-item-title">{es ? 'Póliza' : 'Policy'} · {policy.file.name}</span>
            <button type="button" className="cd-btn cd-btn-invoice" onClick={() => openBackup(policy.file.storageKey).catch(e => toast.error(e.message))}>
              <i className="fa-solid fa-eye"></i> {es ? 'Ver póliza' : 'View policy'}
            </button>
          </div>
          {summary && <details className="policy-details"><summary>{es ? 'Resumen del análisis' : 'Analysis summary'}{policy.status === 'reviewed' ? (es ? ' (revisado)' : ' (reviewed)') : (es ? ' (borrador)' : ' (draft)')}</summary><PolicySummary shown={summary} es={es} /></details>}
        </div>
      )}
      {documents.map(document => (
        <div key={document.id} className="doc-item">
          <div className="doc-item-head">
            <span className="doc-item-title">{document.title}</span>
            <button type="button" className="cd-btn cd-btn-invoice" onClick={() => setPreflight(document.id.toLowerCase())} disabled={generating !== null}>
              <i className="fa-solid fa-file-pdf"></i> {generating === document.id ? (es ? 'Generando…' : 'Generating…') : `${es ? 'Generar' : 'Generate'} ${document.id}`}
            </button>
          </div>
          <DocumentSignature claimRecord={claimRecord} docType={document.id.toLowerCase()} language={language} isAdmin={canSign} onClaimChange={onClaimChange} inDocuments />
          {document.missing.length > 0 ? (
            <p className="doc-missing"><i className="fa-solid fa-triangle-exclamation"></i> {es ? 'Saldrán en blanco: ' : 'Will be left blank: '}{document.missing.map(m => m.label).join(', ')}</p>
          ) : (
            <p className="doc-complete"><i className="fa-solid fa-circle-check"></i> {es ? 'Todos los datos están completos.' : 'All data is complete.'}</p>
          )}
        </div>
      ))}
      {invoices.length > 0 && (
        <div className="doc-item">
          <div className="doc-item-head"><span className="doc-item-title">Invoices</span></div>
          {invoices.map(check => (
            <div key={check.id} className="doc-item-head">
              <span>Invoice #{check.invoiceNumber}{check.checkNumber ? ` · ${es ? 'Cheque' : 'Check'} #${check.checkNumber}` : ''}</span>
              <span>
                <button type="button" className="cd-btn cd-btn-invoice" onClick={() => viewInvoice(check)}><i className="fa-solid fa-file-invoice"></i> {es ? 'Ver invoice' : 'View invoice'}</button>
                <button type="button" className="cd-btn" onClick={() => printInvoice(check)}><i className="fa-solid fa-print"></i> {es ? 'Imprimir / PDF' : 'Print / PDF'}</button>
              </span>
            </div>
          ))}
        </div>
      )}
      {/* Cada Closing se trabaja dentro de su cheque (Fase 4); aquí queda la
          lista completa para consultarla o descargarla, plegada. */}
      <details className="doc-item policy-details">
        <summary>{es ? 'Closing Statements de todos los cheques' : 'Closing Statements for all checks'}</summary>
        <ClosingBuilder claimRecord={claimRecord} language={language} isAdmin={isAdmin} onClaimChange={onClaimChange} mode="list" />
      </details>
    </div>
  );
};

export default ClaimDocuments;
